import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import Swal from "sweetalert2";
import { PageTitle } from "../../../_metronic/layout/core";
import { IEmpresas } from "../../interfaces/IEmpresas";
import EmpresasService from "../../services/EmpresasService";
import { subscribeToEsteira } from "../../services/CardNotificationService";
import { formatarCnpj } from "../../utils/String";
import ImportarRazaoService, {
  ArquivoLeva,
  BoardColuna,
  ETAPA_TEXTO,
  EtapaLeva,
  ImportacaoBoardCard,
  StatusLayout,
  UploadEmIdentificacao,
} from "../../services/ImportarRazaoService";
import ModalEstimativaIA from "./components/ModalEstimativaIA";
import ModalMapeamento from "./components/ModalMapeamento";
import ModalMapeamentoColunas from "./components/ModalMapeamentoColunas";
import ModalProcessamento from "./components/ModalProcessamento";
import ModalResumoImportacao from "./components/ModalResumoImportacao";

// ── Colunas do board (mesmo estilo visual de /integracao) ────────────────────
const COLUNAS: { key: BoardColuna; titulo: string; header: string; body: string }[] = [
  { key: "Processamento", titulo: "Processamento", header: "#F59E0B", body: "#FFF7E8" },
  { key: "Falha", titulo: "Falha", header: "#EF4444", body: "#FEF2F2" },
  { key: "Mapeamento", titulo: "Mapeamento de contas", header: "#0EA5E9", body: "#F0F9FF" },
  { key: "Concluido", titulo: "Concluído", header: "#22C55E", body: "#F0FDF4" },
];

const mesAno = (iso?: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? ""
    : `${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};
const periodoTexto = (ini?: string | null, fim?: string | null) => {
  const i = mesAno(ini);
  const f = mesAno(fim);
  return i || f ? `${i} – ${f}` : "";
};

// DEVN3-602: etapa da identificação de layout exibida no card sintético.
const ETIQUETA_LAYOUT: Partial<Record<StatusLayout, { texto: string; fundo: string; cor: string; dica: string }>> = {
  ANALISANDO: { texto: "ANALISANDO", fundo: "#E0F2FE", cor: "#075985", dica: "Identificando o formato dos arquivos..." },
  AGUARDANDO_CONFIRMACAO_IA: { texto: "CONFIRMAR CUSTO", fundo: "#EDE9FE", cor: "#5B21B6", dica: "Formato novo — clique para ver o custo da IA." },
  IDENTIFICANDO: { texto: "IA IDENTIFICANDO", fundo: "#EDE9FE", cor: "#5B21B6", dica: "A IA está identificando o layout..." },
  AJUSTE_MANUAL: { texto: "AJUSTAR COLUNAS", fundo: "#FEF3C7", cor: "#92400E", dica: "Clique para ajustar o mapeamento das colunas." },
};

/** Card sintético de um upload cujo processamento ainda não tem esteira no board. */
interface UploadEmAndamento {
  uploadId: number;
  empresaId: number;
  empresaNome?: string;
  cnpj?: string;
  esteiraId?: number | null;
  erro: boolean;
  processou: boolean;
  statusLayout?: StatusLayout | null;
  etapa?: EtapaLeva | null;
  erroMensagem?: string | null;
}

/** Status do layout que a leva retoma ao "Tentar novamente", pela etapa em que parou (DEVN3-602). */
const STATUS_AO_RETOMAR: Record<EtapaLeva, StatusLayout> = {
  ANALISAR: "ANALISANDO",
  IDENTIFICAR_LAYOUT: "IDENTIFICANDO",
  SEED: "PROCESSANDO",
  CHUNKS: "PROCESSANDO",
};

const deIdentificacao = (u: UploadEmIdentificacao): UploadEmAndamento => ({
  uploadId: u.uploadId,
  empresaId: u.empresaId ?? 0,
  esteiraId: u.esteiraId ?? null,
  erro: !!u.erro,
  processou: false,
  statusLayout: (u.statusLayout || null) as StatusLayout | null,
  etapa: u.etapa ?? null,
  erroMensagem: u.erroMensagem ?? null,
});

type Modal =
  | { tipo: "processamento"; uploadId: number; empresaNome?: string; voltar?: Modal }
  | { tipo: "estimativa"; uploadId: number; empresaNome?: string }
  | { tipo: "colunas"; uploadId: number; empresaNome?: string }
  | { tipo: "mapeamento"; card: ImportacaoBoardCard }
  | { tipo: "resumo"; card: ImportacaoBoardCard }
  | null;

const ImportarRazao = () => {
  // ── Topo: empresa ─────────────────────────────────────────────────────────
  const [empresas, setEmpresas] = useState<IEmpresas[]>([]);
  const [empresaId, setEmpresaId] = useState<number | null>(null);
  const [modoCadastro, setModoCadastro] = useState(false);
  const [novaEmpresa, setNovaEmpresa] = useState({
    cnpj: "", razaoSocial: "", nomeFantasia: "", municipio: "", estado: "",
  });
  const [salvandoEmpresa, setSalvandoEmpresa] = useState(false);

  // ── Topo: arquivos ───────────────────────────────────────────────────────
  const [arquivos, setArquivos] = useState<ArquivoLeva[]>([]);
  const [enviando, setEnviando] = useState(false);
  const [dragAtivo, setDragAtivo] = useState(false);
  const inputFileRef = useRef<HTMLInputElement>(null);

  // ── Board ────────────────────────────────────────────────────────────────
  const [board, setBoard] = useState<ImportacaoBoardCard[] | null>(null);
  const [uploads, setUploads] = useState<UploadEmAndamento[]>([]);
  const [modal, setModal] = useState<Modal>(null);

  const boardPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const uploadsPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const subsRef = useRef<Map<number, () => void>>(new Map());

  const boardRef = useRef<ImportacaoBoardCard[] | null>(null);
  boardRef.current = board;
  const uploadsRef = useRef<UploadEmAndamento[]>([]);
  uploadsRef.current = uploads;
  // uploads cuja estimativa de custo já abriu sozinha (abre uma vez quando a análise termina)
  const estimativaAutoAbertaRef = useRef<Set<number>>(new Set());
  const modalRef = useRef<Modal>(null);
  modalRef.current = modal;

  const carregarBoard = useCallback(async () => {
    try {
      setBoard(await ImportarRazaoService.board());
    } catch {
      setBoard((b) => b ?? []);
    }
  }, []);

  useEffect(() => {
    EmpresasService.obterTodos().then((e) => setEmpresas(e || [])).catch(() => setEmpresas([]));
    carregarBoard();
    // DEVN3-602: levas paradas na identificação de layout (ex. aguardando confirmação do custo)
    // continuam no board depois de recarregar a página.
    recarregarIdentificacao(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carregarBoard]);

  /**
   * Levas paradas na identificação de layout ou que falharam (coluna Falha) — sobrevivem a recarregar a
   * página e trazem a etapa/mensagem da falha. Na carga inicial não abre a estimativa sozinha.
   */
  function recarregarIdentificacao(cargaInicial = false) {
    ImportarRazaoService.emIdentificacao()
      .then((lista) => {
        const porId = new Map(lista.map((u) => [u.uploadId, u]));
        setUploads((prev) => [
          ...prev.map((p) => {
            const u = porId.get(p.uploadId);
            return u?.erro ? { ...p, erro: true, etapa: u.etapa ?? null, erroMensagem: u.erroMensagem ?? null } : p;
          }),
          ...lista
            .filter((u) => !prev.some((p) => p.uploadId === u.uploadId))
            .map((u) => {
              if (cargaInicial) estimativaAutoAbertaRef.current.add(u.uploadId);
              return deIdentificacao(u);
            }),
        ]);
      })
      .catch(() => {});
  }

  // ── Polling do board enquanto houver algo em processamento ────────────────
  const temProcessando = useMemo(
    () =>
      uploads.some((u) => !u.erro) ||
      (board ?? []).some((c) => c.coluna === "Processamento"),
    [uploads, board]
  );

  useEffect(() => {
    if (!temProcessando) return;
    boardPollRef.current = setInterval(carregarBoard, 15000);
    return () => {
      if (boardPollRef.current) clearInterval(boardPollRef.current);
    };
  }, [temProcessando, carregarBoard]);

  // ── Firestore: move o card em tempo real quando o processamento termina ───
  useEffect(() => {
    const emProcessamento = new Set(
      (board ?? []).filter((c) => c.coluna === "Processamento").map((c) => c.esteiraId)
    );
    uploads.forEach((u) => u.esteiraId && emProcessamento.add(u.esteiraId));

    // assina os que faltam
    emProcessamento.forEach((esteiraId) => {
      if (!subsRef.current.has(esteiraId)) {
        subsRef.current.set(esteiraId, subscribeToEsteira(esteiraId, () => carregarBoard()));
      }
    });
    // cancela os que saíram de processamento
    subsRef.current.forEach((unsub, esteiraId) => {
      if (!emProcessamento.has(esteiraId)) {
        unsub();
        subsRef.current.delete(esteiraId);
      }
    });
  }, [board, uploads, carregarBoard]);

  useEffect(() => {
    const subs = subsRef.current;
    return () => {
      subs.forEach((unsub) => unsub());
      subs.clear();
    };
  }, []);

  // ── Polling dos uploads sintéticos até a esteira aparecer no board ────────
  const temUploads = uploads.length > 0;
  useEffect(() => {
    if (!temUploads) return;

    const tick = async () => {
      const pendentes = uploadsRef.current.filter((u) => !u.processou && !u.erro);
      if (pendentes.length === 0) return;
      const statuses = await Promise.all(
        pendentes.map(async (u) => {
          try {
            const s = await ImportarRazaoService.status(u.uploadId);
            return {
              uploadId: u.uploadId, esteiraId: s.esteiraId ?? null, erro: s.erro, processou: s.processou,
              statusLayout: s.statusLayout ?? null,
            };
          } catch {
            return null;
          }
        })
      );
      const porId = new Map<number, { esteiraId: number | null; erro: boolean; processou: boolean; statusLayout: StatusLayout | null }>();
      statuses.forEach((s) => {
        if (s) porId.set(s.uploadId, { esteiraId: s.esteiraId, erro: s.erro, processou: s.processou, statusLayout: s.statusLayout });
      });

      // Análise terminou com formato novo: abre a estimativa de custo uma vez, se nada estiver aberto.
      porId.forEach((s, uploadId) => {
        if (s.statusLayout === "AGUARDANDO_CONFIRMACAO_IA" && !estimativaAutoAbertaRef.current.has(uploadId) && !modalRef.current) {
          estimativaAutoAbertaRef.current.add(uploadId);
          const u = uploadsRef.current.find((x) => x.uploadId === uploadId);
          setModal({ tipo: "estimativa", uploadId, empresaNome: u?.empresaNome });
        }
      });
      const idsNoBoard = new Set((boardRef.current ?? []).map((c) => c.esteiraId));
      let mudou = false;

      setUploads((prev) =>
        prev
          .map((u) => {
            const s = porId.get(u.uploadId);
            if (!s) return u;
            if (s.erro !== u.erro || s.processou !== u.processou || (s.esteiraId ?? u.esteiraId) !== u.esteiraId) mudou = true;
            return { ...u, esteiraId: s.esteiraId ?? u.esteiraId, erro: s.erro, processou: s.processou, statusLayout: s.statusLayout };
          })
          // sai da lista quando a esteira já está no board, ou processou sem erro e sem esteira própria
          .filter((u) => !(u.esteiraId && idsNoBoard.has(u.esteiraId)) && !(u.processou && !u.esteiraId && !u.erro))
      );

      if (mudou) carregarBoard();
      if (Array.from(porId.values()).some((s) => s.erro)) recarregarIdentificacao();
    };

    tick();
    uploadsPollRef.current = setInterval(tick, 3000);
    return () => {
      if (uploadsPollRef.current) clearInterval(uploadsPollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [temUploads, carregarBoard]);

  // ── Empresa ──────────────────────────────────────────────────────────────
  const opcoesEmpresa = useMemo(
    () =>
      empresas.map((e) => ({
        value: e.id,
        label: `${e.cnpj || ""} — ${e.razaoSocial || e.nomeFantasia || ""}`,
      })),
    [empresas]
  );
  const empresaSelecionada = useMemo(
    () => empresas.find((e) => e.id === empresaId) || null,
    [empresas, empresaId]
  );

  async function salvarEmpresa() {
    if (!/\d{14}/.test(novaEmpresa.cnpj.replace(/\D/g, ""))) {
      Swal.fire({ icon: "warning", text: "Informe um CNPJ com 14 dígitos." });
      return;
    }
    if (!novaEmpresa.razaoSocial.trim()) {
      Swal.fire({ icon: "warning", text: "Informe a razão social." });
      return;
    }
    setSalvandoEmpresa(true);
    try {
      const criada = await ImportarRazaoService.cadastrarEmpresa(novaEmpresa);
      const nova = {
        id: criada.id, cnpj: criada.cnpj, razaoSocial: criada.razaoSocial, nomeFantasia: criada.nomeFantasia,
      } as IEmpresas;
      setEmpresas((prev) => [nova, ...prev]);
      setEmpresaId(criada.id);
      setModoCadastro(false);
      setNovaEmpresa({ cnpj: "", razaoSocial: "", nomeFantasia: "", municipio: "", estado: "" });
      Swal.fire({ icon: "success", text: "Empresa cadastrada.", timer: 2000, showConfirmButton: false });
    } catch (err: any) {
      Swal.fire({ icon: "error", text: err?.message || "Não foi possível cadastrar a empresa." });
    } finally {
      setSalvandoEmpresa(false);
    }
  }

  // ── Arquivos ─────────────────────────────────────────────────────────────
  function adicionarArquivos(fileList: FileList | null) {
    if (!fileList) return;
    const novos = Array.from(fileList).map((file) => ({
      file, originalFileName: file.name, progresso: 0, enviado: false,
    }));
    setArquivos((prev) => [...prev, ...novos]);
  }

  async function enviarEProcessar() {
    const erro = ImportarRazaoService.validarSelecao(arquivos.map((a) => a.file));
    if (erro) {
      Swal.fire({ icon: "warning", text: erro });
      return;
    }
    if (!empresaId) {
      Swal.fire({ icon: "warning", text: "Selecione a empresa da importação." });
      return;
    }

    setEnviando(true);
    try {
      const refs = await ImportarRazaoService.enviarArquivos(arquivos, (i, patch) =>
        setArquivos((prev) => prev.map((a, idx) => (idx === i ? { ...a, ...patch } : a)))
      );
      const { uploadId } = await ImportarRazaoService.iniciarImportacao({ arquivos: refs, empresaId });

      setUploads((prev) => [
        ...prev,
        {
          uploadId,
          empresaId,
          empresaNome: empresaSelecionada?.razaoSocial || empresaSelecionada?.nomeFantasia,
          cnpj: empresaSelecionada?.cnpj,
          esteiraId: null,
          erro: false,
          processou: false,
          statusLayout: "ANALISANDO",
        },
      ]);
      setArquivos([]);
      carregarBoard();
      Swal.fire({
        icon: "success",
        text: "Arquivos enviados. Estamos identificando o formato — acompanhe o card em Processamento.",
        timer: 2500,
        showConfirmButton: false,
      });
    } catch (err: any) {
      Swal.fire({ icon: "error", text: err?.message || "Falha ao iniciar a importação." });
    } finally {
      setEnviando(false);
    }
  }

  // ── Cards por coluna (board + sintéticos) ─────────────────────────────────
  const cardsPorColuna = useMemo(() => {
    const r: Record<BoardColuna, { card?: ImportacaoBoardCard; upload?: UploadEmAndamento; key: string }[]> = {
      Processamento: [], Falha: [], Mapeamento: [], Concluido: [],
    };
    (board ?? []).forEach((c) => r[c.coluna].push({ card: c, key: `e-${c.esteiraId}` }));

    const idsNoBoard = new Set((board ?? []).map((c) => c.esteiraId));
    uploads.forEach((u) => {
      if (u.esteiraId && idsNoBoard.has(u.esteiraId)) return; // já está no board
      (u.erro ? r.Falha : r.Processamento).push({ upload: u, key: `u-${u.uploadId}` });
    });
    return r;
  }, [board, uploads]);

  function nomeEmpresa(u: UploadEmAndamento) {
    if (u.empresaNome) return u.empresaNome;
    const e = empresas.find((x) => x.id === u.empresaId);
    return e?.razaoSocial || e?.nomeFantasia;
  }

  function abrirCard(entry: { card?: ImportacaoBoardCard; upload?: UploadEmAndamento }) {
    if (entry.upload) {
      const u = entry.upload;
      const tipo = u.erro ? "processamento"
        : u.statusLayout === "AGUARDANDO_CONFIRMACAO_IA" ? "estimativa"
        : u.statusLayout === "AJUSTE_MANUAL" ? "colunas"
        : "processamento";
      setModal({ tipo, uploadId: u.uploadId, empresaNome: nomeEmpresa(u) });
      return;
    }
    const c = entry.card!;
    if (c.coluna === "Falha" || c.coluna === "Processamento")
      // o board traz o upload de cada card; o da sessão fica de reserva para API antiga
      setModal({ tipo: "processamento", uploadId: c.uploadId ?? uploadDaEsteira(c.esteiraId), empresaNome: c.empresaNome });
    else if (c.coluna === "Mapeamento") setModal({ tipo: "mapeamento", card: c });
    else setModal({ tipo: "resumo", card: c });
  }

  // ── Coluna Falha (DEVN3-602) ────────────────────────────────────────────
  const [acaoFalha, setAcaoFalha] = useState<number | null>(null);

  async function retentar(uploadId: number, etapa?: EtapaLeva | null, esteiraId?: number | null) {
    setAcaoFalha(uploadId);
    try {
      await ImportarRazaoService.retentar(uploadId);
      const status = etapa ? STATUS_AO_RETOMAR[etapa] : "PROCESSANDO";
      setUploads((prev) => {
        const existe = prev.some((u) => u.uploadId === uploadId);
        const base = prev.map((u) =>
          u.uploadId === uploadId ? { ...u, erro: false, processou: false, statusLayout: status, erroMensagem: null } : u
        );
        // card de falha que veio do board (esteira já criada): acompanha pelo polling até sair de Processamento
        return existe ? base : [...base, { uploadId, empresaId: 0, esteiraId: esteiraId ?? null, erro: false, processou: false, statusLayout: status }];
      });
      carregarBoard();
    } catch (err: any) {
      Swal.fire({ icon: "error", text: err?.message || "Não foi possível tentar novamente." });
    } finally {
      setAcaoFalha(null);
    }
  }

  async function descartar(uploadId: number) {
    const r = await Swal.fire({
      icon: "question",
      text: "Descartar esta importação? Ela sai do board e nada mais é processado.",
      showCancelButton: true,
      confirmButtonText: "Sim, descartar",
      cancelButtonText: "Voltar",
    });
    if (!r.isConfirmed) return;
    setAcaoFalha(uploadId);
    try {
      await ImportarRazaoService.descartar(uploadId);
      setUploads((prev) => prev.filter((u) => u.uploadId !== uploadId));
      carregarBoard();
    } catch (err: any) {
      Swal.fire({ icon: "error", text: err?.message || "Não foi possível descartar." });
    } finally {
      setAcaoFalha(null);
    }
  }

  // uploadId do card em processamento que veio do board (se conhecido nesta sessão)
  function uploadDaEsteira(esteiraId: number): number {
    return uploads.find((u) => u.esteiraId === esteiraId)?.uploadId ?? 0;
  }

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      <PageTitle breadcrumbs={[]}>Importar Razão Universal</PageTitle>

      {/* Painel do topo — seleção/cadastro de empresa + envio de arquivo */}
      <div className="card mb-4" style={{ borderRadius: 12, border: "none", boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}>
        <div className="card-body p-4">
          <div className="row g-4">
            <div className="col-lg-5">
              <label className="form-label fw-bold">Empresa</label>
              {!modoCadastro ? (
                <>
                  <Select
                    classNamePrefix="react-select"
                    placeholder="Selecione a empresa..."
                    options={opcoesEmpresa}
                    value={opcoesEmpresa.find((o) => o.value === empresaId) || null}
                    onChange={(o: any) => setEmpresaId(o?.value ?? null)}
                  />
                  <button type="button" className="btn btn-link px-0 mt-1" onClick={() => setModoCadastro(true)}>
                    + Cadastrar nova empresa
                  </button>
                </>
              ) : (
                <div className="row g-2">
                  <div className="col-12">
                    <input className="form-control form-control-sm" placeholder="CNPJ *"
                      value={novaEmpresa.cnpj}
                      onChange={(e) => setNovaEmpresa({ ...novaEmpresa, cnpj: e.target.value })} />
                  </div>
                  <div className="col-12">
                    <input className="form-control form-control-sm" placeholder="Razão social *"
                      value={novaEmpresa.razaoSocial}
                      onChange={(e) => setNovaEmpresa({ ...novaEmpresa, razaoSocial: e.target.value })} />
                  </div>
                  <div className="col-7">
                    <input className="form-control form-control-sm" placeholder="Município (opcional)"
                      value={novaEmpresa.municipio}
                      onChange={(e) => setNovaEmpresa({ ...novaEmpresa, municipio: e.target.value })} />
                  </div>
                  <div className="col-5">
                    <input className="form-control form-control-sm" placeholder="UF (opcional)" maxLength={2}
                      value={novaEmpresa.estado}
                      onChange={(e) => setNovaEmpresa({ ...novaEmpresa, estado: e.target.value.toUpperCase() })} />
                  </div>
                  <div className="col-12 d-flex gap-2">
                    <button className="btn btn-sm btn-primary" onClick={salvarEmpresa} disabled={salvandoEmpresa}>
                      {salvandoEmpresa ? "Salvando..." : "Salvar"}
                    </button>
                    <button className="btn btn-sm btn-light" onClick={() => setModoCadastro(false)}>Cancelar</button>
                  </div>
                </div>
              )}
            </div>

            <div className="col-lg-7">
              <label className="form-label fw-bold">Arquivo do razão (Excel, CSV ou um .zip)</label>
              <div
                onClick={() => inputFileRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragAtivo(true); }}
                onDragLeave={() => setDragAtivo(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragAtivo(false);
                  adicionarArquivos(e.dataTransfer.files);
                }}
                style={{
                  border: `2px dashed ${dragAtivo ? "#0EA5E9" : "#CBD5E1"}`,
                  borderRadius: 12,
                  background: dragAtivo ? "#F0F9FF" : "#F8FAFC",
                  padding: "22px 16px",
                  textAlign: "center",
                  cursor: "pointer",
                  transition: "background 0.15s, border-color 0.15s",
                }}
              >
                <i className="fas fa-file-excel fs-2 text-success mb-2 d-block" />
                <span className="text-muted">
                  Arraste o arquivo aqui ou <span className="text-primary fw-bold">clique para selecionar</span>
                </span>
                <input
                  ref={inputFileRef}
                  type="file"
                  className="d-none"
                  multiple
                  accept=".xlsx,.xls,.csv,.zip"
                  onChange={(e) => { adicionarArquivos(e.target.files); e.target.value = ""; }}
                />
              </div>

              {arquivos.length > 0 && (
                <ul className="list-group mt-2">
                  {arquivos.map((a, i) => (
                    <li key={i} className="list-group-item d-flex justify-content-between align-items-center py-2">
                      <span className="text-truncate">{a.originalFileName}</span>
                      <span className="d-flex align-items-center gap-2">
                        {a.erro ? <span className="text-danger">{a.erro}</span>
                          : a.enviado ? <span className="text-success">enviado</span>
                          : a.progresso > 0 ? <span className="text-muted">{a.progresso}%</span> : null}
                        <button className="btn btn-sm btn-link text-danger p-0" disabled={enviando}
                          onClick={() => setArquivos((prev) => prev.filter((_, idx) => idx !== i))}>
                          remover
                        </button>
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              <button
                className="btn btn-primary mt-3"
                onClick={enviarEProcessar}
                disabled={enviando || arquivos.length === 0 || !empresaId}
              >
                {enviando ? "Enviando..." : "Importar"}
              </button>
              <div className="form-text mt-1">
                Qualquer layout de razão: o formato é identificado automaticamente (formatos novos pedem
                confirmação do custo da IA). O período é deduzido das datas dos lançamentos.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Board de steps */}
      <div className="d-flex justify-content-end mb-3">
        <button className="btn btn-sm btn-light" onClick={carregarBoard}>&#x21bb; Atualizar</button>
      </div>

      <div className="card" style={{ borderRadius: 14, border: "1px solid #E4E6EF", boxShadow: "0 2px 10px rgba(15,23,42,0.04)" }}>
        <div className="card-body" style={{ padding: 18 }}>
          {board === null ? (
            <div className="text-center text-muted py-10">Carregando o board...</div>
          ) : (
            <div style={{ overflowX: "auto", paddingBottom: 12 }}>
              <div style={{ display: "flex", gap: 14, minWidth: "max-content", alignItems: "stretch" }}>
                {COLUNAS.map((col) => {
                  const cards = cardsPorColuna[col.key];
                  return (
                    <div key={col.key} style={{
                      width: 300, minWidth: 300, background: col.body, border: "1px solid #DDE3EA",
                      borderRadius: 14, display: "flex", flexDirection: "column", height: 600,
                    }}>
                      <div style={{
                        background: col.header, color: "#fff", padding: "10px 12px", borderRadius: "14px 14px 0 0",
                        display: "flex", alignItems: "center", justifyContent: "space-between", fontWeight: 700, fontSize: 13,
                      }}>
                        <span>{col.titulo}</span>
                        <span style={{
                          minWidth: 24, height: 22, padding: "0 8px", borderRadius: 999,
                          background: "rgba(255,255,255,0.22)", display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 12, fontWeight: 700,
                        }}>{cards.length}</span>
                      </div>

                      <div style={{ padding: 8, flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
                        {cards.length === 0 ? (
                          <div style={{
                            minHeight: 80, border: "1px dashed #D4DCE6", borderRadius: 10,
                            background: "rgba(255,255,255,0.4)", display: "flex", alignItems: "center", justifyContent: "center",
                            color: "#A1A9B6", fontSize: 14,
                          }}>Vazio</div>
                        ) : (
                          cards.map((entry) => {
                            const c = entry.card;
                            const u = entry.upload;
                            const nome = c?.empresaNome || (u && nomeEmpresa(u)) || `Empresa ${c?.empresaId ?? u?.empresaId ?? ""}`;
                            const etiquetaLayout = u && !u.erro && u.statusLayout ? ETIQUETA_LAYOUT[u.statusLayout] : undefined;
                            const cnpj = c?.cnpj || u?.cnpj;
                            const periodo = c ? periodoTexto(c.periodoInicio, c.periodoFim) : "";
                            const erro = u?.erro || c?.coluna === "Falha";
                            const importando = !erro && (!!u || c?.coluna === "Processamento");
                            const uploadFalha = u?.uploadId ?? c?.uploadId ?? null;
                            const etapaFalha = u?.etapa ?? c?.etapa ?? null;
                            const mensagemFalha = u?.erroMensagem ?? c?.erroMensagem ?? null;
                            return (
                              <div key={entry.key}
                                onClick={() => abrirCard(entry)}
                                style={{
                                  background: "#fff", borderRadius: 10,
                                  border: erro ? "2px solid #EF4444" : importando ? "2px solid #F59E0B" : "1px solid #DCE3EA",
                                  boxShadow: "0 1px 4px rgba(15,23,42,0.06)", padding: "10px 12px", cursor: "pointer",
                                }}>
                                <div className="d-flex justify-content-between align-items-start mb-1">
                                  <span style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", color: "#1E293B" }}>
                                    {nome}
                                  </span>
                                  {erro ? (
                                    <span style={{ fontSize: 9, padding: "2px 6px", borderRadius: 4, background: "#FEE2E2", color: "#991B1B", fontWeight: 700 }}>ERRO</span>
                                  ) : etiquetaLayout ? (
                                    <span style={{ fontSize: 9, padding: "2px 6px", borderRadius: 4, background: etiquetaLayout.fundo, color: etiquetaLayout.cor, fontWeight: 700 }}>{etiquetaLayout.texto}</span>
                                  ) : importando ? (
                                    <span style={{ fontSize: 9, padding: "2px 6px", borderRadius: 4, background: "#FEF3C7", color: "#92400E", fontWeight: 700 }}>IMPORTANDO</span>
                                  ) : null}
                                </div>
                                {cnpj && <div style={{ fontSize: 10, color: "#94A3B8" }}>{formatarCnpj(cnpj)}</div>}
                                {periodo && <div style={{ fontSize: 10, color: "#64748B", fontWeight: 600, marginTop: 2 }}>Período: {periodo}</div>}
                                {c && col.key === "Mapeamento" && (
                                  <div style={{ fontSize: 11, color: "#0EA5E9", fontWeight: 700, marginTop: 6 }}>
                                    {c.contasPendentes} conta(s) a mapear
                                  </div>
                                )}
                                {c && col.key === "Concluido" && (
                                  <div style={{ fontSize: 11, color: "#22C55E", fontWeight: 700, marginTop: 6 }}>
                                    {c.contasClassificadas}/{c.totalContas} contas classificadas
                                  </div>
                                )}
                                {erro && (
                                  <div style={{ marginTop: 6 }}>
                                    <div style={{ fontSize: 11, color: "#991B1B", fontWeight: 700 }}>
                                      Falhou em: {etapaFalha ? ETAPA_TEXTO[etapaFalha] : "processamento"}
                                    </div>
                                    {mensagemFalha && (
                                      <div title={mensagemFalha} style={{
                                        fontSize: 10, color: "#7F1D1D", marginTop: 2, overflow: "hidden",
                                        display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical",
                                      }}>{mensagemFalha}</div>
                                    )}
                                    {uploadFalha && (
                                      <div className="d-flex gap-2 mt-2">
                                        <button className="btn btn-sm btn-danger py-1 px-2" style={{ fontSize: 11 }}
                                          disabled={acaoFalha === uploadFalha}
                                          onClick={(e) => { e.stopPropagation(); retentar(uploadFalha, etapaFalha, c?.esteiraId ?? u?.esteiraId); }}>
                                          {acaoFalha === uploadFalha ? "..." : "Tentar novamente"}
                                        </button>
                                        <button className="btn btn-sm btn-light py-1 px-2" style={{ fontSize: 11 }}
                                          disabled={acaoFalha === uploadFalha}
                                          onClick={(e) => { e.stopPropagation(); descartar(uploadFalha); }}>
                                          Descartar
                                        </button>
                                      </div>
                                    )}
                                    <div style={{ fontSize: 10, color: "#94A3B8", marginTop: 4 }}>Clique no card para ver o log.</div>
                                  </div>
                                )}
                                {etiquetaLayout && (
                                  <div style={{ fontSize: 11, color: etiquetaLayout.cor, fontWeight: 600, marginTop: 6 }}>
                                    {etiquetaLayout.dica}
                                  </div>
                                )}
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {modal?.tipo === "processamento" && modal.uploadId > 0 && (
        <ModalProcessamento
          uploadId={modal.uploadId}
          empresaNome={modal.empresaNome}
          onClose={() => { setModal(modal.voltar ?? null); carregarBoard(); }}
        />
      )}
      {modal?.tipo === "processamento" && modal.uploadId === 0 && (
        // card de processamento vindo do board sem uploadId conhecido nesta sessão
        <div style={{
          position: "fixed", inset: 0, background: "rgba(15,23,42,0.4)", zIndex: 9999,
          display: "flex", alignItems: "center", justifyContent: "center",
        }} onClick={() => setModal(null)}>
          <div className="card shadow-lg p-5" style={{ borderRadius: 16 }} onClick={(e) => e.stopPropagation()}>
            <p className="mb-3">Importação em processamento. O card sai desta coluna automaticamente ao terminar.</p>
            <button className="btn btn-sm btn-secondary" onClick={() => setModal(null)}>Fechar</button>
          </div>
        </div>
      )}
      {modal?.tipo === "estimativa" && (
        <ModalEstimativaIA
          uploadId={modal.uploadId}
          empresaNome={modal.empresaNome}
          onConfirmado={() => {
            setUploads((prev) => prev.map((u) => (u.uploadId === modal.uploadId ? { ...u, statusLayout: "IDENTIFICANDO" } : u)));
            setModal({ tipo: "processamento", uploadId: modal.uploadId, empresaNome: modal.empresaNome });
          }}
          onCancelado={() => {
            setUploads((prev) => prev.filter((u) => u.uploadId !== modal.uploadId));
            setModal(null);
          }}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.tipo === "colunas" && (
        <ModalMapeamentoColunas
          uploadId={modal.uploadId}
          empresaNome={modal.empresaNome}
          onConcluido={() => {
            setUploads((prev) => prev.map((u) => (u.uploadId === modal.uploadId ? { ...u, statusLayout: "PROCESSANDO" } : u)));
            setModal({ tipo: "processamento", uploadId: modal.uploadId, empresaNome: modal.empresaNome });
          }}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.tipo === "mapeamento" && (
        <ModalMapeamento
          esteiraId={modal.card.esteiraId}
          empresaNome={modal.card.empresaNome}
          periodo={periodoTexto(modal.card.periodoInicio, modal.card.periodoFim)}
          onClassificou={carregarBoard}
          onVerLog={modal.card.uploadId ? () => setModal({
            tipo: "processamento", uploadId: modal.card.uploadId!, empresaNome: modal.card.empresaNome, voltar: modal,
          }) : undefined}
          onConcluido={() => { setModal(null); carregarBoard(); }}
          onClose={() => { setModal(null); carregarBoard(); }}
        />
      )}
      {modal?.tipo === "resumo" && (
        <ModalResumoImportacao
          card={modal.card}
          periodo={periodoTexto(modal.card.periodoInicio, modal.card.periodoFim)}
          onClose={() => setModal(null)}
        />
      )}
    </>
  );
};

export default ImportarRazao;
